<template>
  <teleport to="#appRoot">
    <div class="fixed inset-0 bg-950">
      <full-screen
        v-if="!showingFloorPlan"
        :title="t('ordering.move-products')"
        @close="$emit('close')"
      >
        <div class="flex w-full h-full overflow-hidden">
          <div
            class="sm:flex hidden flex-col gap-4 px-4 justify-center items-center text-center flex-1 w-0 bg-n-800"
          >
            <img src="@/assets/select-product.svg" class="w-40 h-40" />
            <div class="text-n-0 heading-2">
              {{ t('ordering.move-products-title') }}
            </div>
            <div class="font-body text-base text-n-0">
              {{ t('ordering.move-products-description') }}
            </div>
          </div>
          <div class="w-full sm:w-80 flex flex-col px-4 pt-6 pb-3 h-full bg-n-700">
            <div class="flex flex-col gap-3 overflow-y-auto flex-1">
              <div v-for="(seat, index) in tabSeats" :key="index">
                <div v-if="seat.length" class="font-body text-n-0 border-b border-n-700">
                  <div class="cursor-pointer" @click="selectProducts(index, seat)">
                    <div v-if="index === 0">
                      {{ t('ordering.shared') }}
                    </div>
                    <div v-else>{{ t('ordering.seat') }} {{ index }}</div>
                  </div>
                  <div v-for="product in seat" :key="product.id" class="flex items-center">
                    <l-checkbox
                      :class="{ 'opacity-50': product.notBilledQuantity === 0 }"
                      theme="dark"
                      :model-value="
                        selectedProducts.some(selectedProduct => selectedProduct.id === product.id)
                      "
                      @update:model-value="value => selectProduct(product, value)"
                    />
                    <product-component
                      :product="product"
                      :show-price="false"
                      :disable-swipe="true"
                    />
                  </div>
                </div>
              </div>
            </div>
            <l-button
              class="w-full shrink-0"
              :disabled="selectedProducts.length === 0"
              @click="showingFloorPlan = true"
              >{{
                t('ordering.move-products-cta', {
                  productsNumber: selectedProducts.length
                })
              }}
            </l-button>
          </div>
        </div>
      </full-screen>

      <table-selector
        v-model:is-active="showingFloorPlan"
        :title="t('ordering.move-products')"
        :blocked-tables="blockedTables"
        @close="showingFloorPlan = false"
        @table-selected="tableId => selectTable(tableId)"
      />
      <tab-selector
        v-if="showTabSelector"
        :tab-ids-to-select="toTable.tabIds"
        @tab-selected="tabSelected"
        @close="showTabSelector = false"
      />
      <move-products-confirmation-modal
        v-if="openConfirmationModal"
        :to-table="toTable"
        :to-tab="toTab"
        :from-tab="tabs[fromTabId]"
        :products="selectedProducts"
        @confirm="moveProducts"
        @close="handleCloseModal"
      />
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LCheckbox } from '@last/core-ui/paprika'

import FullScreen from '@/components/FullScreen.vue'
import ProductComponent from '@/components/Product.vue'
import { useKitchenOrders } from '@/composables/useKitchenOrders'
import { useTabs } from '@/composables/useTabs'
import TableSelector from '@/modules/floorplan/TableSelector.vue'
import TabSelector from '@/modules/floorplan/TabSelector.vue'
import MoveProductsConfirmationModal from '@/modules/ordering/MoveProductsConfirmationModal.vue'
import { useFloorplanStore } from '@/store/floorplan'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'

const props = defineProps<{
  fromTabId: string
}>()

const emit = defineEmits<{
  productsMoved: [string]
  close: []
}>()

const { t } = useI18n()

const floorplanStore = useFloorplanStore()
const tabsStore = useTabsStore()
const reservationsStore = useReservationsStore()

const { searchReservation } = storeToRefs(reservationsStore)
const { moveProductTab, openTab, startBilling } = tabsStore
const { tabs } = storeToRefs(tabsStore)

const { seatProducts, sharedProducts, sentToKitchenProducts } = useTabs(props.fromTabId)
const { sendKitchenOrderModifications } = useKitchenOrders(props.fromTabId)
const { allTables } = storeToRefs(floorplanStore)

const selectedSeat = ref<number | null>(null)
const selectedProducts = ref<any[]>([])
const showingFloorPlan = ref(false)
const fromTable = ref<{ id: string | null }>({ id: null })
const toTable = ref<any>(null)
const toTab = ref<any>(null)
const showTabSelector = ref(false)
const tableWithoutTabs = ref(false)
const tabId = ref<string | null>(null)

const tabSeats = computed(() => [sharedProducts.value, ...seatProducts.value])

const openConfirmationModal = computed(
  () => fromTable.value && (toTab.value || tableWithoutTabs.value)
)

const blockedTables = computed(() =>
  allTables.value
    .filter(table => table.id === fromTable.value.id || !!searchReservation.value(table.id))
    .map(table => table.id)
)

onMounted(() => {
  fromTable.value = allTables.value.find(table => table.tabIds.includes(props.fromTabId)) || {
    id: null
  }
})

function handleCloseModal() {
  toTab.value = null
  tableWithoutTabs.value = false
}

function tabSelected(tabId: string) {
  toTab.value = tabs.value[tabId]
}

function selectProduct(product: any, value: boolean) {
  if (product.notBilledQuantity === 0) return
  if (value) selectedProducts.value.push(product)
  else
    selectedProducts.value.splice(
      selectedProducts.value.findIndex(selectedProduct => selectedProduct.id === product.id),
      1
    )
}

function selectProducts(index: number, seat: any[]) {
  if (selectedSeat.value !== index) {
    selectedSeat.value = index
    selectedProducts.value = [
      ...seat.filter(product => product.notBilledQuantity),
      ...selectedProducts.value.filter(
        product => !seat.some(seatProduct => seatProduct.id === product.id)
      )
    ]
  } else {
    selectedSeat.value = null
    selectedProducts.value = selectedProducts.value.filter(
      product => !seat.some(seatProduct => seatProduct.id === product.id)
    )
  }
}

function selectTable(selectedTableId: string): void {
  toTable.value = allTables.value.find(table => table.id === selectedTableId) || null
  if (toTable.value?.tabIds?.length > 1) {
    showTabSelector.value = true
  } else if (toTable.value?.tabIds?.length === 1) {
    toTab.value = tabs.value[toTable.value.tabIds[0]]
  } else {
    tableWithoutTabs.value = true
  }
}

function getTabId() {
  if (toTab.value) return toTab.value.id
  else {
    const toTabId = openTab({ tableIds: [toTable.value.id], tab: {} })
    return toTabId
  }
}

function moveProducts() {
  const toTabId = getTabId()

  const alreadyOrderedProducts = selectedProducts.value.filter(product => {
    const productIds = product.comboProducts?.length
      ? product.comboProducts.map((cp: { id: string }) => cp.id)
      : [product.id]
    return sentToKitchenProducts.value.some(data => productIds.includes(data.productId))
  })

  selectedProducts.value.map(product => {
    moveProductTab({
      productId: product.id,
      fromTabId: props.fromTabId,
      toTabId: toTabId
    })
  })

  tabId.value = props.fromTabId
  sendKitchenOrderModifications(false)

  tabId.value = toTabId
  const { sendKitchenOrderByProducts } = useKitchenOrders(toTabId)
  sendKitchenOrderByProducts(alreadyOrderedProducts, false)

  emit('productsMoved', toTabId)
  emit('close')
  // Start billing destination tab if origin tab already has some bill to avoid fraud
  if (!tabs.value[toTabId].billingStartedTime && tabs.value[props.fromTabId].billingStartedTime) {
    startBilling(toTabId)
  }
}
</script>
